import { useEffect } from "react";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { skipToken } from "@reduxjs/toolkit/query";
import { DateTime } from "luxon";
import { getReportSnapshot } from "@/api/report/getReportSnapshot";
import { formatDateTime } from "@/app/date.utils";
import { NoteAlert } from "@/components/NoteAlert";
import { JobRunDayField } from "@/features/job/job-run/JobRunDayField";
import type { JobRunSettingsProps } from "@/features/job/job-run/jobRun.types";
import { toJobRunInputWith, toJobRunText } from "@/features/job/job-run/jobRun.utils";
import { BUILD_SNAPSHOT_REPORTS, toBuildSnapshotTarget } from "./jobRunBuildSnapshot.utils";

/** Build snapshot's settings: the report and the day. Warns when that day's snapshot exists, and asks before replacing it. */
export const JobRunBuildSnapshot = (props: JobRunSettingsProps) => {
  const { input, onChange, onConfirmChange } = props;
  const report = toJobRunText(input, "report");
  const date = toJobRunText(input, "date");
  const target = toBuildSnapshotTarget(input);
  const snapshotQuery = getReportSnapshot.useQuery(target ?? skipToken);
  const existing = target ? snapshotQuery.data : null;
  const reportLabel = BUILD_SNAPSHOT_REPORTS.find((option) => option.value === report)?.label ?? report;
  let dayText = "";
  if (date) dayText = DateTime.fromFormat(date, "yyMMdd").toFormat("d LLL yyyy");
  let builtText = "";
  if (existing) builtText = formatDateTime(existing.builtAt);

  useEffect(() => {
    if (!existing) {
      onConfirmChange(null);
      return;
    }
    onConfirmChange({
      title: `Replace the ${reportLabel} snapshot for ${dayText}?`,
      body: `The one built ${builtText} will be overwritten.`,
      confirmLabel: "Replace and run",
    });
  }, [existing, reportLabel, dayText, builtText, onConfirmChange]);

  const handleReportChange = (value: string) => {
    const next = toJobRunInputWith(input, "report", value);
    onChange(next);
  };

  const handleDateChange = (value: string) => {
    const next = toJobRunInputWith(input, "date", value);
    onChange(next);
  };

  return (
    <Stack spacing={2}>
      <TextField
        fullWidth
        label="Report"
        onChange={(event) => handleReportChange(event.target.value)}
        select
        size="small"
        value={report}
      >
        {BUILD_SNAPSHOT_REPORTS.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </TextField>
      <JobRunDayField
        disableFuture
        format="yyMMdd"
        label="Day"
        onChange={handleDateChange}
        value={date}
      />
      {existing && (
        <NoteAlert severity="warning">
          A {reportLabel} snapshot for {dayText} already exists (built {builtText}). Running again replaces it.
        </NoteAlert>
      )}
    </Stack>
  );
};
